import React, { useMemo } from "react";
import EmptyChart from "../ui/EmptyChart";

/**
 * Heatmap — week × hour grid where each cell's opacity encodes a count.
 *
 * Props:
 *   matrix: number[7][24] — outer index = day-of-week (0=Mon), inner = hour
 *
 * Caller supplies the matrix only; the component picks its own `max` for
 * intensity scaling. Tooltips on each cell expose the underlying value.
 */
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const heatColor = (intensity) => {
  // intensity 0..1 → translucent brand orange
  const a = Math.max(0.05, intensity);
  return `rgba(255, 140, 0, ${a})`;
};

const Heatmap = ({ matrix }) => {
  const max = useMemo(() => {
    let m = 0;
    for (const row of matrix || []) for (const v of row) if (v > m) m = v;
    return Math.max(1, m);
  }, [matrix]);

  if (!matrix || matrix.length === 0) {
    return <EmptyChart label="No data in range" height={120} />;
  }

  return (
    <>
      <div className="flex gap-2">
        <div className="flex flex-col justify-between py-1 text-[10px] text-on-surface-light-variant dark:text-on-surface-variant">
          {DAY_LABELS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div
          className="flex-1 grid gap-[2px]"
          style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}
        >
          {matrix.flatMap((row, dayIdx) =>
            row.map((val, hour) => {
              const intensity = val / max;
              return (
                <div
                  key={`${dayIdx}-${hour}`}
                  title={`${DAY_LABELS[dayIdx]} ${String(hour).padStart(2, "0")}:00 — ${val} calls`}
                  className="aspect-square rounded-[2px]"
                  style={{ background: heatColor(intensity) }}
                />
              );
            }),
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1 text-[10px] text-on-surface-light-variant dark:text-on-surface-variant">
        <span>0h</span>
        <div className="flex-1" />
        <span>12h</span>
        <div className="flex-1" />
        <span>23h</span>
      </div>
    </>
  );
};

export default Heatmap;
